import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Storage as DatabaseIcon,
  TableChart as TableIcon,
  ViewColumn as ColumnIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Assessment as StatsIcon,
  Code as QueryIcon,
  Download as ExportIcon,
  ExpandLess,
  ExpandMore,
  Delete as DeleteIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { databaseAPI } from "../services/api.js";
import SyncService from "../services/syncService.js";
import { useAuth } from "../contexts/AuthContext.jsx";
import { format } from "date-fns";

const DatabaseManager = () => {
  const { isAdmin } = useAuth();
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [queryDialog, setQueryDialog] = useState(false);
  const [customQuery, setCustomQuery] = useState("");
  const [queryResults, setQueryResults] = useState(null);
  const [expandedDatabases, setExpandedDatabases] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null,
    item: null,
  });
  const [cleanupDialog, setCleanupDialog] = useState(false);
  const [expandedSchemas, setExpandedSchemas] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  if (!isAdmin()) {
    return (
      <Alert severity="error">
        You need administrator privileges to access this page.
      </Alert>
    );
  }

  // Fetch databases
  const {
    data: databasesData,
    isLoading: databasesLoading,
    error: databasesError,
  } = useQuery({
    queryKey: ["databases-admin"],
    queryFn: () => databaseAPI.listDatabases({ include_tables: true }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch table details when selected
  const { data: tableDetailsData, isLoading: tableDetailsLoading } = useQuery({
    queryKey: [
      "table-details",
      selectedTable?.schema_name,
      selectedTable?.table_name,
    ],
    queryFn: () =>
      databaseAPI.getSchemaTablesColumns([
        `${selectedTable.schema_name}.${selectedTable.table_name}`,
      ]),
    enabled: !!(selectedTable && selectedTable.schema_name && selectedTable.table_name),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Admin: list all schemas
  const { data: schemasData, isLoading: schemasLoading, error: schemasError } =
    useQuery({
      queryKey: ["admin-schemas"],
      queryFn: () => databaseAPI.listSchemas(),
      enabled: isAdmin(),
      staleTime: 5 * 60 * 1000,
    });

  // Split schemas
  const allSchemas = schemasData?.data?.schemas || [];
  const adminSchema = allSchemas.find((s) => s.is_admin);
  const nonAdminSchemas = allSchemas.filter((s) => !s.is_admin);

  const schemaNames = nonAdminSchemas.map((s) => s.schema_name);

  const { data: schemaTablesData, isLoading: schemaTablesLoading } = useQuery({
    queryKey: ["admin-schema-tables", schemaNames.join(",")],
    queryFn: () => databaseAPI.getSchemaTables(schemaNames),
    enabled: isAdmin() && schemaNames.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  // Admin: fetch tables for admin schema separately
  const { data: adminSchemaTablesData, isLoading: adminSchemaTablesLoading } =
    useQuery({
      queryKey: ["admin-schema-tables-admin", adminSchema?.schema_name || "none"],
      queryFn: () => databaseAPI.getSchemaTables([adminSchema.schema_name]),
      enabled: isAdmin() && !!adminSchema,
      staleTime: 2 * 60 * 1000,
    });

  // Execute custom query mutation
  const executeQueryMutation = useMutation({
    mutationFn: (queryData) => databaseAPI.executeQuery(queryData),
    onSuccess: (data) => {
      setQueryResults(data.data);
      enqueueSnackbar("Query executed successfully", { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to execute query",
        { variant: "error" }
      );
    },
  });

  // Delete database mutation
  const deleteDatabaseMutation = useMutation({
    mutationFn: (databaseName) => databaseAPI.deleteDatabase(databaseName),
    onSuccess: (data) => {
      SyncService.syncAfterDatabaseChange();
      enqueueSnackbar(
        `Database deleted successfully. ${data.data.deleted_uploads} related uploads removed.`,
        { variant: "success" }
      );
      setDeleteDialog({ open: false, type: null, item: null });
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to delete database",
        { variant: "error" }
      );
    },
  });

  // Delete table mutation
  const deleteTableMutation = useMutation({
    mutationFn: ({ databaseName, tableName }) =>
      databaseAPI.deleteTable(databaseName, tableName),
    onSuccess: (data) => {
      SyncService.syncAfterDatabaseChange();
      enqueueSnackbar(
        `Table deleted successfully. ${data.data.deleted_uploads} related uploads removed.`,
        { variant: "success" }
      );
      setDeleteDialog({ open: false, type: null, item: null });
      // Clear selected table if it was deleted
      if (selectedTable?.table_name === data.data.table_name) {
        setSelectedTable(null);
        setTabValue(0);
      }
    },
    onError: (error) => {
      enqueueSnackbar(error.response?.data?.error || "Failed to delete table", {
        variant: "error",
      });
    },
  });

  // Cleanup orphaned data mutation
  const cleanupMutation = useMutation({
    mutationFn: () => databaseAPI.cleanupOrphanedData(),
    onSuccess: (data) => {
      SyncService.syncAll();
      enqueueSnackbar(
        `Cleanup completed. ${data.data.orphaned_uploads_cleaned} orphaned uploads removed, ${data.data.schemas_synced || data.data.databases_synced} schemas synced.`,
        { variant: "success" }
      );
      setCleanupDialog(false);
    },
    onError: (error) => {
      enqueueSnackbar(error.response?.data?.error || "Failed to cleanup data", {
        variant: "error",
      });
    },
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["databases-admin"] });
    enqueueSnackbar("Data refreshed", { variant: "success" });
  };

  const handleDatabaseToggle = (databaseName) => {
    setExpandedDatabases((prev) => ({
      ...prev,
      [databaseName]: !prev[databaseName],
    }));
  };

  const handleSchemaToggle = (schemaName) => {
    setExpandedSchemas((prev) => ({
      ...prev,
      [schemaName]: !prev[schemaName],
    }));
  };

  const handleTableSelect = (database, table) => {
    setSelectedDatabase(database);
    setSelectedTable(table);
    setTabValue(1); // Switch to table details tab
  };

  const handleExecuteQuery = () => {
    if (!customQuery.trim()) {
      enqueueSnackbar("Please enter a query", { variant: "warning" });
      return;
    }

    executeQueryMutation.mutate({
      query: customQuery,
      limit: 100, // Limit results for safety
    });
  };

  const handleDeleteDatabase = (database) => {
    setDeleteDialog({
      open: true,
      type: "database",
      item: database,
    });
  };

  const handleDeleteTable = (database, table) => {
    setDeleteDialog({
      open: true,
      type: "table",
      item: { database, table },
    });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.type === "database") {
      deleteDatabaseMutation.mutate(deleteDialog.item.database_name);
    } else if (deleteDialog.type === "table") {
      deleteTableMutation.mutate({
        databaseName: deleteDialog.item.database.database_name,
        tableName: deleteDialog.item.table.table_name,
      });
    }
  };

  const handleCleanup = () => {
    setCleanupDialog(true);
  };

  const handleConfirmCleanup = () => {
    cleanupMutation.mutate();
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const databases = databasesData?.data?.databases || [];
  const tableColumns = (() => {
    const map = tableDetailsData?.data?.table_columns || {};
    const key = selectedTable?.schema_name && selectedTable?.table_name ? `${selectedTable.schema_name}.${selectedTable.table_name}` : null;
    return key && map[key] ? map[key].columns || [] : [];
  })();
  const defaultDatabase = databases[0] || null;

  // Group fetched tables by schema for admin view
  const schemaTables = (schemaTablesData?.data?.tables || []).reduce(
    (acc, tbl) => {
      const schema = tbl.schema_name;
      if (!acc[schema]) acc[schema] = [];
      acc[schema].push(tbl);
      return acc;
    },
    {}
  );

  const adminSchemaTables = (adminSchemaTablesData?.data?.tables || []).reduce(
    (acc, tbl) => {
      const schema = tbl.schema_name;
      if (!acc[schema]) acc[schema] = [];
      acc[schema].push(tbl);
      return acc;
    },
    {}
  );

  if (databasesLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Database Manager
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage databases, tables, and view data structure
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<QueryIcon />}
            onClick={() => setQueryDialog(true)}
          >
            Custom Query
          </Button>
          <Button
            variant="outlined"
            startIcon={<WarningIcon />}
            onClick={handleCleanup}
            disabled={cleanupMutation.isLoading}
            color="warning"
          >
            Cleanup Orphaned Data
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={databasesLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {databasesError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load databases:{" "}
          {databasesError.response?.data?.error || databasesError.message}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DatabaseIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {databases.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Databases
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <TableIcon color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {(() => {
                      const schemaCount = (schemasData?.data?.schemas || []).length;
                      return schemaCount;
                    })()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Schemas
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <StatsIcon color="info" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {formatNumber(
                      schemasData?.data?.total_records ||
                        (schemasData?.data?.schemas || []).reduce(
                          (sum, s) => sum + (s.record_count || 0),
                          0
                        )
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Records
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <ColumnIcon color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {formatNumber(
                      schemasData?.data?.total_columns ||
                        (schemasData?.data?.schemas || []).reduce(
                          (sum, s) => sum + (s.column_count || 0),
                          0
                        )
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Columns
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Card>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
        >
          <Tab label="Database Overview" />
          <Tab label="Table Details" disabled={!selectedTable} />
        </Tabs>

        {/* Database Overview Tab */}
        {tabValue === 0 && (
          <CardContent>
            {databases.length > 0 ? (
              <List>
                {databases.map((database) => (
                  <Box key={database.id}>
                    <ListItem
                      button
                      onClick={() =>
                        handleDatabaseToggle(database.database_name)
                      }
                      sx={{ borderRadius: 1, mb: 1, bgcolor: "grey.50" }}
                    >
                      <ListItemIcon>
                        <DatabaseIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Typography variant="h6" fontWeight="bold">
                              {database.database_name}
                            </Typography>
                            <Chip
                              label={`${database.total_tables || 0} tables`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                            <Chip
                              label={`${formatNumber(
                                database.total_records || 0
                              )} records`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1 }}
                            >
                              {database.description ||
                                "No description available"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Created:{" "}
                              {format(
                                new Date(database.created_at),
                                "MMM dd, yyyy HH:mm"
                              )}
                            </Typography>
                          </React.Fragment>
                        }
                        primaryTypographyProps={{ component: "div" }}
                        secondaryTypographyProps={{ component: "div" }}
                      />
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {expandedDatabases[database.database_name] ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </Box>
                    </ListItem>

                    <Collapse
                      in={expandedDatabases[database.database_name]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                        {/* Schemas list within this database (replaces table list) */}
                        {isAdmin() && (
                          <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                              Schemas in {database.database_name}
                            </Typography>
                            {schemasLoading ? (
                              <Box display="flex" justifyContent="center" py={2}>
                                <CircularProgress size={20} />
                              </Box>
                            ) : allSchemas.length > 0 ? (
                              <List>
                                {allSchemas.map((schema) => (
                                  <ListItem
                                    key={`${database.database_name}-${schema.schema_name}`}
                                    sx={{ borderRadius: 1, mb: 1, bgcolor: "grey.50" }}
                                  >
                                    <ListItemIcon>
                                      <DatabaseIcon color={schema.is_admin ? "warning" : "secondary"} />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                          <Typography variant="body1" fontWeight="bold">
                                            {schema.schema_name}
                                          </Typography>
                                          <Chip label={schema.is_admin ? "Admin" : "User"} size="small" color={schema.is_admin ? "warning" : "default"} />
                                          <Chip label={`user: ${schema.username}`} size="small" variant="outlined" />
                                        </Box>
                                      }
                                      secondary={
                                        <Typography variant="caption" color="text.secondary">
                                          {schema.email}
                                        </Typography>
                                      }
                                      primaryTypographyProps={{ component: "div" }}
                                      secondaryTypographyProps={{ component: "div" }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                No schemas found
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <DatabaseIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Databases Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No databases have been created yet. Upload some Excel files to
                  get started.
                </Typography>
              </Box>
            )}

            {/* Admin: User Schemas Overview */}
            {isAdmin() && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  User Schemas
                </Typography>

                {schemasError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    Failed to load user schemas: {schemasError.message}
                  </Alert>
                )}

                {schemasLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : nonAdminSchemas.length > 0 ? (
                  <List>
                    {nonAdminSchemas.map((schema) => (
                      <Box key={schema.schema_name}>
                        <ListItem
                          button
                          onClick={() => handleSchemaToggle(schema.schema_name)}
                          sx={{ borderRadius: 1, mb: 1, bgcolor: "grey.50" }}
                        >
                          <ListItemIcon>
                            <DatabaseIcon color="secondary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Typography variant="h6" fontWeight="bold">
                                  {schema.schema_name}
                                </Typography>
                                <Chip
                                  label={`${schema.table_count || 0} tables`}
                                  size="small"
                                  color="secondary"
                                  variant="outlined"
                                />
                                <Chip
                                  label={`user: ${schema.username}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {schema.email}
                              </Typography>
                            }
                            primaryTypographyProps={{ component: "div" }}
                            secondaryTypographyProps={{ component: "div" }}
                          />
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {expandedSchemas[schema.schema_name] ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )}
                          </Box>
                        </ListItem>

                        <Collapse in={expandedSchemas[schema.schema_name]} timeout="auto" unmountOnExit>
                          <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                            {/* Tables Section with capped height and scrolling */}
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              Tables
                            </Typography>
                            {schemaTablesLoading ? (
                              <Box display="flex" justifyContent="center" py={2}>
                                <CircularProgress size={20} />
                              </Box>
                            ) : (schemaTables[schema.schema_name] || []).length > 0 ? (
                              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Table Name</TableCell>
                                      <TableCell align="right">Records</TableCell>
                                      <TableCell align="right">Columns</TableCell>
                                      <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(schemaTables[schema.schema_name] || []).map((tbl) => (
                                      <TableRow key={tbl.id} hover>
                                        <TableCell>
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <TableIcon fontSize="small" color="action" />
                                            <Typography variant="body2" fontWeight="medium">
                                              {tbl.table_name}
                                            </Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2">
                                            {formatNumber(tbl.record_count || 0)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                          <Typography variant="body2">{tbl.column_count || 0}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                            <Tooltip title="View Table Details">
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleTableSelect(
                                                    defaultDatabase || { database_name: "google_db" },
                                                    tbl
                                                  )
                                                }
                                              >
                                                <InfoIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Table">
                                              <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() =>
                                                  handleDeleteTable(
                                                    defaultDatabase || { database_name: "google_db" },
                                                    { table_name: tbl.table_name }
                                                  )
                                                }
                                                disabled={deleteTableMutation.isLoading}
                                              >
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                No tables found in this schema
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No user schemas found
                  </Typography>
                )}
              </Box>
            )}

            {/* Admin Schema Details */}
            {isAdmin() && adminSchema && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Admin Schema
                </Typography>
                <List>
                  <Box key={adminSchema.schema_name}>
                    <ListItem
                      button
                      onClick={() => handleSchemaToggle(adminSchema.schema_name)}
                      sx={{ borderRadius: 1, mb: 1, bgcolor: "grey.50" }}
                    >
                      <ListItemIcon>
                        <DatabaseIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Typography variant="h6" fontWeight="bold">
                              {adminSchema.schema_name}
                            </Typography>
                            <Chip
                              label={`${adminSchema.table_count || 0} tables`}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                            <Chip label={`user: ${adminSchema.username}`} size="small" variant="outlined" />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {adminSchema.email}
                          </Typography>
                        }
                        primaryTypographyProps={{ component: "div" }}
                        secondaryTypographyProps={{ component: "div" }}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {expandedSchemas[adminSchema.schema_name] ? <ExpandLess /> : <ExpandMore />}
                      </Box>
                    </ListItem>

                        <Collapse in={expandedSchemas[adminSchema.schema_name]} timeout="auto" unmountOnExit>
                      <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                        {/* Admin tables with capped height and scrolling */}
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Tables
                        </Typography>
                        {adminSchemaTablesLoading ? (
                          <Box display="flex" justifyContent="center" py={2}>
                            <CircularProgress size={20} />
                          </Box>
                        ) : (adminSchemaTables[adminSchema.schema_name] || []).length > 0 ? (
                          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Table Name</TableCell>
                                  <TableCell align="right">Records</TableCell>
                                  <TableCell align="right">Columns</TableCell>
                                  <TableCell align="center">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(adminSchemaTables[adminSchema.schema_name] || []).map((tbl) => (
                                  <TableRow key={tbl.id} hover>
                                    <TableCell>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <TableIcon fontSize="small" color="action" />
                                        <Typography variant="body2" fontWeight="medium">
                                          {tbl.table_name}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2">{formatNumber(tbl.record_count || 0)}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography variant="body2">{tbl.column_count || 0}</Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                        <Tooltip title="View Table Details">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleTableSelect(
                                                defaultDatabase || { database_name: "google_db" },
                                                {
                                                  id: tbl.id,
                                                  table_name: tbl.table_name,
                                                  record_count: tbl.record_count,
                                                  column_count: tbl.column_count,
                                                }
                                              )
                                            }
                                          >
                                            <InfoIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                            No tables found in admin schema
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                </List>
              </Box>
            )}
          </CardContent>
        )}

        {/* Table Details Tab */}
        {tabValue === 1 && selectedTable && (
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {selectedDatabase.database_name}.{selectedTable.table_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedTable.description || "No description available"}
              </Typography>
            </Box>

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Records
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {formatNumber(selectedTable.record_count || 0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="success.main" gutterBottom>
                      Columns
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {selectedTable.column_count || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="info.main" gutterBottom>
                      Last Updated
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedTable.updated_at
                        ? format(
                            new Date(selectedTable.updated_at),
                            "MMM dd, yyyy"
                          )
                        : "N/A"}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Column Details */}
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Column Schema
            </Typography>

            {tableDetailsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : tableColumns.length > 0 ? (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Column Name</TableCell>
                      <TableCell>Data Type</TableCell>
                      <TableCell align="center">Nullable</TableCell>
                      <TableCell align="center">Primary Key</TableCell>
                      <TableCell>Default Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableColumns.map((column, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <ColumnIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight="medium">
                              {column.column_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={column.data_type}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={column.is_nullable ? "Yes" : "No"}
                            size="small"
                            color={column.is_nullable ? "warning" : "success"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {column.is_primary_key && (
                            <Chip
                              label="PK"
                              size="small"
                              color="error"
                              variant="filled"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {column.default_value || "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No column information available
              </Typography>
            )}
          </CardContent>
        )}
      </Card>

      {/* Custom Query Dialog */}
      <Dialog
        open={queryDialog}
        onClose={() => setQueryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Execute Custom Query</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="SQL Query"
            fullWidth
            multiline
            rows={6}
            value={customQuery}
            onChange={(e) => setCustomQuery(e.target.value)}
            placeholder="SELECT * FROM your_table LIMIT 10;"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <Alert severity="warning" sx={{ mb: 2 }}>
            Only SELECT queries are allowed. Results are limited to 100 rows for
            performance.
          </Alert>

          {queryResults && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Query Results ({queryResults.results?.length || 0} rows)
              </Typography>
              {queryResults.results && queryResults.results.length > 0 && (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 300 }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {Object.keys(queryResults.results[0]).map((column) => (
                          <TableCell key={column}>
                            <Typography variant="body2" fontWeight="bold">
                              {column}
                            </Typography>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {queryResults.results.map((row, index) => (
                        <TableRow key={index} hover>
                          {Object.values(row).map((value, cellIndex) => (
                            <TableCell key={cellIndex}>
                              <Typography variant="body2">
                                {value !== null ? String(value) : "-"}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQueryDialog(false)}>Cancel</Button>
          <Button
            onClick={handleExecuteQuery}
            variant="contained"
            disabled={executeQueryMutation.isLoading || !customQuery.trim()}
            startIcon={
              executeQueryMutation.isLoading ? (
                <CircularProgress size={16} />
              ) : (
                <QueryIcon />
              )
            }
          >
            Execute Query
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: null, item: null })}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
        disableEnforceFocus={false}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="error" />
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          {deleteDialog.type === "database" && (
            <Box>
              <Typography gutterBottom>
                Are you sure you want to delete the database{" "}
                <strong>"{deleteDialog.item?.database_name}"</strong>?
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                This will:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2">
                  Delete the entire database and all its tables
                </Typography>
                <Typography component="li" variant="body2">
                  Remove all related upload records
                </Typography>
                <Typography component="li" variant="body2">
                  Cancel any running upload tasks
                </Typography>
                <Typography component="li" variant="body2">
                  Delete all physical files associated with uploads
                </Typography>
              </Box>
            </Box>
          )}
          {deleteDialog.type === "table" && (
            <Box>
              <Typography gutterBottom>
                Are you sure you want to delete the table{" "}
                <strong>"{deleteDialog.item?.table?.table_name}"</strong> from
                database{" "}
                <strong>"{deleteDialog.item?.database?.database_name}"</strong>?
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                This will:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                <Typography component="li" variant="body2">
                  Delete the table and all its data
                </Typography>
                <Typography component="li" variant="body2">
                  Remove all related upload records
                </Typography>
                <Typography component="li" variant="body2">
                  Cancel any running upload tasks for this table
                </Typography>
                <Typography component="li" variant="body2">
                  Delete all physical files associated with uploads to this
                  table
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteDialog({ open: false, type: null, item: null })
            }
            disabled={
              deleteDatabaseMutation.isLoading || deleteTableMutation.isLoading
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={
              deleteDatabaseMutation.isLoading || deleteTableMutation.isLoading
            }
            startIcon={
              deleteDatabaseMutation.isLoading ||
              deleteTableMutation.isLoading ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {deleteDatabaseMutation.isLoading || deleteTableMutation.isLoading
              ? "Deleting..."
              : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cleanup Confirmation Dialog */}
      <Dialog
        open={cleanupDialog}
        onClose={() => setCleanupDialog(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
        disableEnforceFocus={false}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="warning" />
          Cleanup Orphaned Data
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will clean up inconsistent data across your application.
          </Alert>
          <Box>
            <Typography gutterBottom>This operation will:</Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, mb: 2 }}>
              <Typography component="li" variant="body2">
                Remove upload records that reference non-existent databases or
                tables
              </Typography>
              <Typography component="li" variant="body2">
                Synchronize database statistics with actual data
              </Typography>
              <Typography component="li" variant="body2">
                Clean up orphaned files from the file system
              </Typography>
              <Typography component="li" variant="body2">
                Cancel any stale background tasks
              </Typography>
            </Box>
            <Typography variant="body2">
              This is a safe operation that helps maintain data consistency.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCleanupDialog(false)}
            disabled={cleanupMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCleanup}
            variant="contained"
            color="warning"
            disabled={cleanupMutation.isLoading}
            startIcon={
              cleanupMutation.isLoading ? (
                <CircularProgress size={16} />
              ) : (
                <WarningIcon />
              )
            }
          >
            {cleanupMutation.isLoading ? "Cleaning up..." : "Cleanup"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DatabaseManager;
